// src/context/NotificationContext.js
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import notificationSound from '../assets/notification.mp3';
import axios from '../utils/axiosHandler';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState(null);
  const [socket, setSocket] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Use refs instead of state for counts to prevent re-renders
  const countsRef = useRef({
    newTickets: 0,
    newLeads: 0
  });

  // Add this useEffect to fetch unread notification count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const response = await axios.get('/api/notifications/count/unread', {
          headers: { Authorization: `Bearer ${token}` }
        });

        
        if (response.status === 200) {
          const data = await response.data;
          setUnreadCount(data.count || 0);
        }
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };

    fetchUnreadCount();
    
    // Set up interval to periodically refresh the count
    const intervalId = setInterval(fetchUnreadCount, 60000); // Check every minute
    
    return () => clearInterval(intervalId);
  }, []);

  // Initialize socket connection
  useEffect(() => {
    console.log('Initializing socket connection');
    
    // Create socket connection - explicitly use the server URL
    const serverUrl = window.location.origin // Use your actual server URL and port
    console.log('Connecting to socket.io server at:', serverUrl);
        
    const newSocket = io(serverUrl, {
      transports: ['polling', 'websocket'], // Try polling first, then WebSockets
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
      path: '/socket.io'
    });
    

    newSocket.on('connect', () => {
      console.log('Socket successfully connected with ID:', newSocket.id);
      
      // Authenticate immediately if token exists
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const payload = JSON.parse(window.atob(base64));
          
          if (payload.userId) {
            const tenantId = localStorage.getItem('tenantId') || 'default';
            const userRole = payload.isAdmin ? 'admin' : 'user';
            
            console.log('Authenticating socket with userId:', payload.userId, 'role:', userRole, 'tenant:', tenantId);
            newSocket.emit('authenticate', payload.userId, userRole, tenantId);
            
            // If user is admin, also emit admin authentication
            if (payload.isAdmin) {
              newSocket.emit('admin-authenticate', payload.userId, tenantId);
            }
          }
        }
      } catch (error) {
        console.error('Error authenticating socket:', error);
      }
    });

    console.log('Socket.io environment setup:', {
      serverUrl,
      protocol: window.location.protocol,
      host: window.location.host,
      pathname: window.location.pathname
    });
    
    // Add additional event listeners
    newSocket.on('connect_failed', () => {
      console.error('Socket connection failed');
    });
    
    newSocket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`Socket reconnection attempt #${attemptNumber}`);
    });
    
    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
    });
    
    newSocket.on('connect_timeout', () => {
      console.error('Socket connection timeout');
    });
    
    newSocket.on('error', (error) => {
      console.error('Socket error:', error);
    });
    
    newSocket.on('disconnected', (reason) => {
      console.log('Socket disconnected:', reason);
    });
    
    setSocket(newSocket);
  
    // Clean up function
    return () => {
      console.log('Cleaning up socket connection');
      newSocket.disconnect();
    };
  }, []);


  // Authenticate socket when user logs in
  useEffect(() => {
    if (!socket) return;

    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      // Decode token to get user ID (assuming JWT)
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));
      
      if (payload.userId) {
        const tenantId = localStorage.getItem('tenantId') || 'default';
        const userRole = payload.isAdmin ? 'admin' : 'user';
        
        socket.emit('authenticate', payload.userId, userRole, tenantId);
        
        // If user is admin, also emit admin authentication
        if (payload.isAdmin) {
          socket.emit('admin-authenticate', payload.userId, tenantId);
        }
      }
    } catch (error) {
      console.error('Error authenticating socket:', error);
    }
  }, [socket]);

  // Handle real-time notifications
  useEffect(() => {
    if (!socket) return;

    // Listen for new notifications
    socket.on('new-notification', (notificationData) => {
      // Play notification sound
      playNotificationSound();
      
      // Show the notification popup
      showNotification('info', notificationData.message);
      
      // Dispatch custom event to trigger bell animation in any component listening
      window.dispatchEvent(new CustomEvent('new-notification-received'));
      
      // Update the appropriate counter based on notification type
      if (notificationData.type === 'lead') {
        countsRef.current.newLeads += 1;
      } else if (notificationData.type === 'ticket') {
        countsRef.current.newTickets += 1;
      }
      
      // If you want to show a browser notification
      if (Notification.permission === 'granted') {
        new Notification('New Notification', {
          body: notificationData.message
        });
      }
    });
    

    // Listen for other notification types
    socket.on('admin-notification', (data) => {
      showNotification('info', data.message);
      playNotificationSound();
    });

    // Listen for auction-specific notifications
    socket.on('auction-bid', (data) => {
      const message = `New bid: $${data.amount.toLocaleString()} for ${data.auction} by ${data.bidder}`;
      showNotification('info', message);
      playNotificationSound();
    });

    socket.on('auction-buynow', (data) => {
      const message = `URGENT: Buy now request for ${data.auction} at $${data.amount.toLocaleString()} by ${data.buyer}`;
      showNotification('warning', message);
      playNotificationSound();
    });

    socket.on('bid-status-update', (data) => {
      const status = data.type === 'bid_approved' ? 'approved' : 'rejected';
      const message = `Your bid of $${data.amount.toLocaleString()} for ${data.auction} has been ${status}`;
      showNotification(data.type === 'bid_approved' ? 'success' : 'error', message);
      playNotificationSound();
    });

    socket.on('purchase-status-update', (data) => {
      const status = data.type === 'purchase_approved' ? 'approved' : 'rejected';
      const message = `Your purchase request for ${data.auction} has been ${status}`;
      showNotification(data.type === 'purchase_approved' ? 'success' : 'error', message);
      playNotificationSound();
    });

    socket.on('auction-outbid', (data) => {
      const message = `You've been outbid on ${data.auction}. New highest: $${data.newHighestBid.toLocaleString()}`;
      showNotification('warning', message);
      playNotificationSound();
    });

    return () => {
      socket.off('new-notification');
      socket.off('admin-notification');
      socket.off('auction-bid');
      socket.off('auction-buynow');
      socket.off('bid-status-update');
      socket.off('purchase-status-update');
      socket.off('auction-outbid');
    };
  }, [socket]);

  const playNotificationSound = () => {
    const audio = new Audio(notificationSound);
    audio.play().catch(error => {
      // Handle any autoplay restrictions
      console.error('Error playing notification sound:', error);
    });
  };

  const showNotification = (type, message, duration = 5000) => {
    // Format the notification consistently whether from API or socket
    const formattedNotification = {
      _id: `temp-${Date.now()}`,
      type: type,
      message: message,
      // Add other properties your notification system might need
    };
    
    setNotification(formattedNotification);
    
    if (duration) {
      setTimeout(() => setNotification(null), duration);
    }
  };
  
  const updateCount = (type, count) => {
    countsRef.current[type] = count;
    // If this is for leads or tickets, also update the unread count
    if (type === 'newLeads' || type === 'newTickets') {
      setUnreadCount(prev => prev + count);
    }
  };
  
  // Update functions now modify the ref without causing re-renders
  const updateTicketCount = (count) => {
    countsRef.current.newTickets = count;
  };

  const updateLeadCount = (count) => {
    countsRef.current.newLeads = count;
  };

  // Getter function to read current counts
  const getCount = (type) => {
    return countsRef.current[type] || 0;
  };

  return (
    <NotificationContext.Provider 
      value={{ 
        notification, 
        showNotification, 
        updateTicketCount, 
        updateLeadCount,
        getCount,
        unreadCount,
        playNotificationSound,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);









// // live working properly
// // src/context/NotificationContext.js
// import React, { createContext, useState, useContext, useRef, useEffect } from 'react';
// import io from 'socket.io-client';

// const NotificationContext = createContext();

// export const NotificationProvider = ({ children }) => {
//   const [notification, setNotification] = useState(null);
//   const [socket, setSocket] = useState(null);
  
//   // Use refs instead of state for counts to prevent re-renders
//   const countsRef = useRef({
//     newTickets: 0,
//     newLeads: 0
//   });

//   // Initialize socket connection
//   useEffect(() => {
//     console.log('Initializing socket connection');
    
//     // Create socket connection - explicitly use the server URL
//     const serverUrl = window.location.origin // Use your actual server URL and port
//     console.log('Connecting to socket.io server at:', serverUrl);
    
//     // const newSocket = io(serverUrl, {
//     //   transports: ['websocket', 'polling'],
//     //   reconnectionAttempts: 5,
//     //   reconnectionDelay: 1000,
//     //   timeout: 20000,
//     //   path: '/socket.io' // Make sure this matches server side
//     // });
    
//     const newSocket = io(serverUrl, {
//       transports: ['polling', 'websocket'], // Try polling first, then WebSockets
//       reconnectionAttempts: 5,
//       reconnectionDelay: 1000,
//       timeout: 20000,
//       path: '/socket.io'
//     });
    

//     newSocket.on('connect', () => {
//       console.log('Socket successfully connected with ID:', newSocket.id);
      
//       // Authenticate immediately if token exists
//       try {
//         const token = localStorage.getItem("token");
//         if (token) {
//           const base64Url = token.split('.')[1];
//           const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
//           const payload = JSON.parse(window.atob(base64));
          
//           if (payload.userId) {
//             console.log('Authenticating socket with userId:', payload.userId);
//             newSocket.emit('authenticate', payload.userId);
//           }
//         }
//       } catch (error) {
//         console.error('Error authenticating socket:', error);
//       }
//     });

//     console.log('Socket.io environment setup:', {
//       serverUrl,
//       protocol: window.location.protocol,
//       host: window.location.host,
//       pathname: window.location.pathname
//     });
    
//     // Add additional event listeners
//     newSocket.on('connect_failed', () => {
//       console.error('Socket connection failed');
//     });
    
//     newSocket.on('reconnect_attempt', (attemptNumber) => {
//       console.log(`Socket reconnection attempt #${attemptNumber}`);
//     });
    
//     newSocket.on('connect_error', (error) => {
//       console.error('Socket connection error:', error.message);
//     });
    
//     newSocket.on('connect_timeout', () => {
//       console.error('Socket connection timeout');
//     });
    
//     newSocket.on('error', (error) => {
//       console.error('Socket error:', error);
//     });
    
//     newSocket.on('disconnected', (reason) => {
//       console.log('Socket disconnected:', reason);
//     });
    
//     setSocket(newSocket);
  
//     // Clean up function
//     return () => {
//       console.log('Cleaning up socket connection');
//       newSocket.disconnect();
//     };
//   }, []);

//   // Authenticate socket when user logs in
//   useEffect(() => {
//     if (!socket) return;

//     const token = localStorage.getItem("token");
//     if (!token) return;

//     try {
//       // Decode token to get user ID (assuming JWT)
//       const base64Url = token.split('.')[1];
//       const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
//       const payload = JSON.parse(window.atob(base64));
      
//       if (payload.userId) {
//         socket.emit('authenticate', payload.userId);
//       }
//     } catch (error) {
//       console.error('Error authenticating socket:', error);
//     }
//   }, [socket]);

//   // Handle real-time notifications
//   useEffect(() => {
//     if (!socket) return;

//     // Listen for new notifications
//     socket.on('new-notification', (notificationData) => {
//       // Show the notification popup
//       showNotification('info', notificationData.message);
      
//       // Update the appropriate counter based on notification type
//       if (notificationData.type === 'lead') {
//         countsRef.current.newLeads += 1;
//       } else if (notificationData.type === 'ticket') {
//         countsRef.current.newTickets += 1;
//       }
      
//       // If you want to show a browser notification
//       if (Notification.permission === 'granted') {
//         new Notification('New Notification', {
//           body: notificationData.message
//         });
//       }
//     });

//     // Listen for other notification types
//     socket.on('admin-notification', (data) => {
//       showNotification('info', data.message);
//     });

//     return () => {
//       socket.off('new-notification');
//       socket.off('admin-notification');
//     };
//   }, [socket]);

//   const showNotification = (type, message, duration = 5000) => {
//     // Format the notification consistently whether from API or socket
//     const formattedNotification = {
//       _id: `temp-${Date.now()}`,
//       type: type,
//       message: message,
//       // Add other properties your notification system might need
//     };
    
//     setNotification(formattedNotification);
    
//     if (duration) {
//       setTimeout(() => setNotification(null), duration);
//     }
//   };

//   // Update functions now modify the ref without causing re-renders
//   const updateTicketCount = (count) => {
//     countsRef.current.newTickets = count;
//   };

//   const updateLeadCount = (count) => {
//     countsRef.current.newLeads = count;
//   };

//   // Getter function to read current counts
//   const getCount = (type) => {
//     return countsRef.current[type] || 0;
//   };

//   return (
//     <NotificationContext.Provider 
//       value={{ 
//         notification, 
//         showNotification, 
//         updateTicketCount, 
//         updateLeadCount,
//         getCount
//       }}
//     >
//       {children}
//     </NotificationContext.Provider>
//   );
// };

// export const useNotification = () => useContext(NotificationContext);














// // before live
// // src/context/NotificationContext.js
// import React, { createContext, useState, useContext, useRef } from 'react';

// const NotificationContext = createContext();

// export const NotificationProvider = ({ children }) => {
//   const [notification, setNotification] = useState(null);
  
//   // Use refs instead of state for counts to prevent re-renders
//   const countsRef = useRef({
//     newTickets: 0,
//     newLeads: 0
//   });

//   const showNotification = (type, message, duration = 5000) => {
//     setNotification({ type, message });
//     if (duration) {
//       setTimeout(() => setNotification(null), duration);
//     }
//   };

//   // Update functions now modify the ref without causing re-renders
//   const updateTicketCount = (count) => {
//     countsRef.current.newTickets = count;
//   };

//   const updateLeadCount = (count) => {
//     countsRef.current.newLeads = count;
//   };

//   // Getter function to read current counts
//   // const getCount = (type) => {
//   //   return countsRef.current[type] || 0;
//   // };
  
//   const getCount = (type) => {
//     return countsRef.current[type] || 0;
//   };


//   return (
//     <NotificationContext.Provider 
//       value={{ 
//         notification, 
//         showNotification, 
//         updateTicketCount, 
//         updateLeadCount,
//         getCount
//       }}
//     >
//       {children}
//     </NotificationContext.Provider>
//   );
// };

// export const useNotification = () => useContext(NotificationContext);

















// // src/context/NotificationContext.js
// import React, { createContext, useState, useContext } from 'react';

// const NotificationContext = createContext();

// export const NotificationProvider = ({ children }) => {
//   const [notification, setNotification] = useState(null);

//   const showNotification = (type, message, duration = 5000) => {
//     setNotification({ type, message });
//     if (duration) {
//       setTimeout(() => setNotification(null), duration);
//     }
//   };

//   return (
//     <NotificationContext.Provider value={{ notification, showNotification }}>
//       {children}
//     </NotificationContext.Provider>
//   );
// };

// export const useNotification = () => useContext(NotificationContext);
